import React, { useRef, useState, useEffect } from 'react';
import { Typography } from '../ui/typography';
import { Button } from "@/components/ui/button";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import CrossIcon from '@/assets/svg/crossIcon.svg';
import CrossDarkIcon from '@/assets/svg/crossDarkIcon.svg';

export interface BannerNotificationDto {
  id: string;
  displayText: string;
  type: 'ADT' | 'RISK';
  createdAt: string; // ISO 8601 format for frontend
}

interface BannerAlertProps {
  message: string;
  onViewAlert?: () => void;
  onDismiss?: () => void;
  type?: string; // Banner type from API (e.g., 'ADT', 'RISK', or any card type)
  onScrollToCard?: (type: string) => void;
}

const BannerAlert: React.FC<BannerAlertProps> = ({
  message,
  onViewAlert,
  onDismiss,
  type,
  onScrollToCard,
}) => {
  const handleViewAlert = () => {
    if (type && onScrollToCard) {
      onScrollToCard(type);
    } else if (onViewAlert) {
      onViewAlert();
    }
  };
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    
    return () => window.removeEventListener('resize', checkTruncation);
  }, [message]);

  return (
    <div className="flex items-center justify-between h-[42px] px-4 bg-red-50 dark:bg-blue-700 border border-red-900 rounded-lg">
      {/* Message */}
      <div className="flex items-center gap-3 flex-1 min-w-0 pr-3">
        <div className="flex items-center justify-center w-5 h-5 flex-shrink-0">
          <i className="ri-error-warning-line text-red-900 dark:text-grey-100 text-[20px]"></i>
        </div>

        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span ref={textRef} className="block min-w-0">
  <Typography 
    variant='semibold-14' 
    className='text-red-900 dark:text-grey-100 truncate block whitespace-nowrap overflow-hidden text-ellipsis'
  >
    {message}
  </Typography>
</span>

            </TooltipTrigger>
            {isTruncated && (
              <TooltipContent
                side='top'
                className='bg-gray-700 text-grey-100 z-50 max-w-md'
              >
                <p className='text-sm'>{message}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Buttons */}
      <div className="flex items-center gap-2 flex-shrink-0">
        
        {/* View Alert Button */}
        {(onViewAlert || (type && onScrollToCard)) && (
          <Button
            variant="alert"
            onClick={handleViewAlert}
            className="h-[30px] px-3 rounded flex items-center whitespace-nowrap dark:text-red-500"
          >
            <Typography variant="medium-12">
              View Alert
            </Typography>
          </Button>
        )}

        {/* Close Button */}
        {onDismiss && (
        <button
            onClick={onDismiss}
            className="flex items-center justify-center w-[18px] h-[18px] rounded bg-transparent hover:bg-transparent flex-shrink-0"
            aria-label="Dismiss alert"
            type="button"
        >
            <img
            src={CrossIcon}
            alt="close"
            className="block dark:hidden w-[10px] h-[10px] min-w-[10px] min-h-[10px]"
            />
            <img
            src={CrossDarkIcon}
            alt="close-dark"
            className="hidden dark:block w-[10px] h-[10px] min-w-[10px] min-h-[10px]"
            />
        </button>
        )}

      </div>
    </div>
  );
};

export default BannerAlert;